import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import TopBar from '../components/chrome/TopBar';
import { LoadingSpinner, ShareSheet } from '../components';
import { eventCardUrl } from '../utils/shareCard';
import PartyStarterShareModal from '../components/PartyStarterShareModal';
import { Pill, RingCircle, C, PASTEL_WALL } from '../components/portalkit';
import { eventService } from '../services';
import { useUser, useAuth } from '../context';
import useMediaQuery from '../utils/useMediaQuery';
import { shareLink } from '../utils/nativeShare';
import { BRAND_URL } from '../utils/brand';

const LIVE_STATUSES = new Set(['selling', 'funding', 'processing_greenlight', 'greenlit']);
const FLASH_MS = 1600;

const SearchGlyph = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>;
const ShareGlyph = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M12 3v13M7 8l5-5 5 5M5 14v7h14v-7" /></svg>;
const Check = ({ size = 12, w = 4 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={w} strokeLinecap="square" aria-hidden="true"><path d="M4 12l5 5L20 6" /></svg>;

const fmtDate = (iso) => {
  if (!iso) return 'Date TBD';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Date TBD';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/**
 * Event Published / share — `/host/events/:id/share` (design_handoff_auth_profile_planner §6,
 * Sept 27 2026). The pastel wall under the black Top Header, a 600px column: breadcrumb, the ring
 * hero, the event card, EVENT LINK (copy the link or the code), SEND TO FRIENDS ON PORTAL (pick,
 * then one invitation each into their inbox), SHARE TO… (the OS sheet where it exists, else the
 * Share Sheet) and GO TO EVENT DASHBOARD.
 *
 * The handoff flagged its own board saying "live" over a DRAFT pill. Reconciled: the hero reads
 * from the event's real status — YOUR EVENT IS LIVE! only once it is, DRAFT SAVED! otherwise —
 * and the pill on the card says the same thing.
 */
export default function EventSharePage() {
  const navigate = useNavigate();
  const { id: eventId } = useParams();
  const location = useLocation();
  const phone = useMediaQuery('(max-width: 699px)');
  const { profile, walletBalance } = useUser();
  const { user, logout } = useAuth();

  const [event, setEvent] = useState(location.state?.event || null);
  const [isLoading, setIsLoading] = useState(!event);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [psShareOpen, setPsShareOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const timers = useRef({});

  const [friends, setFriends] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(0);
  const [sendError, setSendError] = useState(null);

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  useEffect(() => {
    if (event || !eventId) return;
    (async () => {
      setIsLoading(true);
      const r = await eventService.getEvent(eventId);
      if (r.success) setEvent(r.data); else setError(r.error || 'Failed to load event');
      setIsLoading(false);
    })();
  }, [event, eventId]);

  const loadFriends = useCallback(async (id) => {
    setFriendsLoading(true);
    const r = await eventService.getInvitableFriends(id);
    if (r.success) setFriends((r.data || []).filter((f) => f.userId));
    setFriendsLoading(false);
  }, []);
  useEffect(() => { if (event?.id) loadFriends(event.id); }, [event?.id, loadFriends]);

  const isLive = location.state?.published ?? LIVE_STATUSES.has(event?.status);
  const code = event?.inviteCode || eventId;
  const url = `${BRAND_URL}/events/${code}`;

  const flash = (key, setter) => { setter(true); clearTimeout(timers.current[key]); timers.current[key] = setTimeout(() => setter(false), FLASH_MS); };
  const copy = async (text, key, setter) => {
    try { await navigator.clipboard?.writeText(text); } catch { /* the text is on screen to select */ }
    flash(key, setter);
  };

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return friends;
    return friends.filter((f) => `${f.displayName || ''} ${f.handle || ''}`.toLowerCase().includes(s));
  }, [friends, q]);

  const toggle = (userId) => { setSent(0); setSendError(null); setPicked((p) => (p.includes(userId) ? p.filter((x) => x !== userId) : [...p, userId])); };
  const send = async () => {
    if (!event?.id || picked.length === 0 || sending) return;
    setSending(true); setSendError(null);
    let ok = 0; let firstError = null;
    const done = new Set();
    for (const userId of picked) {
      // eslint-disable-next-line no-await-in-loop
      const r = await eventService.inviteFriend(event.id, userId);
      if (r.success) { ok += 1; done.add(userId); } else firstError = firstError || r.error;
    }
    setFriends((prev) => prev.map((f) => (done.has(f.userId) ? { ...f, status: 'invited' } : f)));
    setPicked((p) => p.filter((id) => !done.has(id)));
    setSending(false);
    setSent(ok);
    if (firstError) setSendError(firstError);
  };

  const shareTo = async () => {
    let presented = false;
    try { presented = await shareLink({ title: event?.title || 'Portal', url }); } catch { presented = false; }
    if (!presented) setShareOpen(true);
  };

  const handleLogout = async () => { await logout(); navigate('/'); };
  const chrome = <TopBar user={profile || user} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/featured')} />;
  const page = (children) => (
    <div className="pk" style={{ minHeight: '100dvh', background: PASTEL_WALL, color: '#000', overflowX: 'clip' }}>
      {chrome}
      {children}
    </div>
  );

  if (isLoading) return page(<div style={{ display: 'flex', justifyContent: 'center', padding: '96px 16px' }}><LoadingSpinner size="lg" label="Loading event..." /></div>);
  if (error) {
    return page(
      <main style={{ maxWidth: 600, margin: '0 auto', padding: '72px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>Error</h1>
        <p style={{ margin: 0, fontSize: 13, color: C.gray600 }}>{error}</p>
        <Pill variant="primary" on="white" size={40} onClick={() => navigate('/host')}>GO TO DASHBOARD</Pill>
      </main>,
    );
  }

  const anyPicked = picked.length > 0;
  const n = picked.length;
  const cardPad = phone ? 16 : 20;
  const card = { display: 'flex', flexDirection: 'column', gap: 12, padding: cardPad, borderRadius: 24, background: '#fff' };
  const smallLabel = { fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase' };
  const statusPill = (bg, fg = '#000') => ({ height: 24, padding: '0 10px', display: 'flex', alignItems: 'center', borderRadius: 999, background: bg, color: fg, fontSize: 9, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase' });
  const status = (event?.status || 'draft').replace(/_/g, ' ');
  const live = LIVE_STATUSES.has(event?.status);

  return page(
    <>
      <main style={{ maxWidth: 600, margin: '0 auto', padding: phone ? '16px 16px 32px' : '28px 24px 72px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase' }}>
          <button type="button" onClick={() => navigate('/host')} className="pk pk-btn pk-crumb" style={{ flexShrink: 0, height: 30, padding: '0 12px', background: 'rgba(255,255,255,.7)', color: C.gray500, fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>DASHBOARD</button>
          <span style={{ color: C.gray400 }} aria-hidden="true">/</span>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event?.title}</span>
        </nav>

        {/* Hero */}
        <div className="pk-rise-slow" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: phone ? '12px 0 6px' : '20px 0 10px', textAlign: 'center' }}>
          <RingCircle size={72} border={3} check={28} stroke={2.6} />
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.2em', color: C.gray500, textTransform: 'uppercase' }}>{isLive ? 'EVENT PUBLISHED!' : 'DRAFT SAVED'}</span>
          <h1 style={{ margin: 0, fontSize: phone ? 26 : 34, fontWeight: 800, letterSpacing: '.08em', lineHeight: 1.05, textTransform: 'uppercase' }}>{isLive ? 'YOUR EVENT IS LIVE!' : 'DRAFT SAVED!'}</h1>
          <p style={{ margin: 0, maxWidth: 420, fontSize: 13, lineHeight: 1.65, color: C.gray600, textWrap: 'pretty' }}>
            {isLive ? 'Share your event with potential guests. They can apply using the link or code below.' : 'Your event is saved as a draft. Publish it when you’re ready from the event dashboard, then share it from here.'}
          </p>
        </div>

        {/* Event card */}
        <button type="button" onClick={() => navigate(`/events/${code}`)} className="pk pk-card-outline" style={{ display: 'grid', gridTemplateColumns: `${phone ? 84 : 112}px minmax(0,1fr)`, gap: 16, alignItems: 'center', padding: '10px 18px 10px 10px', border: 'none', borderRadius: 24, background: '#fff', color: '#000', textAlign: 'left', cursor: 'pointer', transition: 'box-shadow .2s', fontFamily: 'inherit' }}>
          <span aria-hidden="true" style={{ display: 'block', width: '100%', aspectRatio: '1 / 1', borderRadius: 16, background: event?.posterUrl ? `url(${event.posterUrl}) center/cover` : C.field }} />
          <span style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '.06em', lineHeight: 1.3, textTransform: 'uppercase' }}>{event?.title}</span>
            <span style={{ fontSize: 12, color: C.gray500 }}>{fmtDate(event?.date || event?.dateTime)}</span>
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <span style={statusPill(live ? C.greenBg : C.held, live ? C.greenFg : '#000')}>{live ? `LIVE · ${status}` : status}</span>
              <span style={statusPill(C.field)}>{event?.mode === 'curated' ? 'CURATED' : 'OPEN'}</span>
            </span>
          </span>
        </button>

        {/* EVENT LINK */}
        <section style={card}>
          <span style={smallLabel}>EVENT LINK</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 5px 5px 16px', borderRadius: 999, background: C.field }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }} data-testid="event-url">{url}</span>
            <button type="button" onClick={() => copy(url, 'link', setCopiedLink)} className="pk pk-btn" style={{ flexShrink: 0, height: 38, padding: '0 16px', background: copiedLink ? C.cyan : '#000', color: copiedLink ? '#000' : '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase' }}>{copiedLink ? 'COPIED' : 'COPY LINK'}</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ ...smallLabel, fontSize: 10, color: C.gray500 }}>OR SHARE THE CODE</span>
            <button type="button" onClick={() => copy(code, 'code', setCopiedCode)} aria-label={`Copy code ${code}`} className="pk pk-btn" style={{ height: 34, padding: '0 6px 0 14px', gap: 10, background: '#000', color: '#fff', fontSize: 14, fontWeight: 800, letterSpacing: '.24em' }}>
              {code}
              <span style={{ height: 24, padding: '0 9px', display: 'flex', alignItems: 'center', borderRadius: 999, background: '#262626', fontSize: 9, letterSpacing: '.14em', fontWeight: 700, color: copiedCode ? C.cyan : C.onBlack3 }}>{copiedCode ? 'COPIED' : 'COPY'}</span>
            </button>
          </div>
        </section>

        {/* SEND TO FRIENDS ON PORTAL */}
        <section style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={smallLabel}>SEND TO FRIENDS ON PORTAL</span>
            {anyPicked && <button type="button" onClick={() => { setPicked([]); setSent(0); }} className="pk" style={{ padding: 0, border: 'none', background: 'none', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: C.gray500, cursor: 'pointer' }}>CLEAR</button>}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, height: 46, padding: '0 16px', border: '2px solid transparent', borderRadius: 999, background: C.field, boxSizing: 'border-box' }}>
            <SearchGlyph />
            <input className="pk-in pk-in-light" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search friends..." aria-label="Search friends" style={{ flex: 1, fontSize: 13 }} />
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {friendsLoading && <span style={{ padding: '16px 8px', fontSize: 12, color: C.gray500 }}>Loading your friends…</span>}
            {!friendsLoading && visible.map((f) => {
              const on = picked.includes(f.userId);
              const busy = f.status !== 'none';
              return (
                <button key={f.userId} type="button" onClick={() => !busy && toggle(f.userId)} aria-pressed={on} disabled={busy} className={`pk ${busy ? '' : 'pk-row-hover-light'}`} style={{ display: 'grid', gridTemplateColumns: '44px minmax(0,1fr) auto', alignItems: 'center', gap: 12, width: '100%', padding: '8px 10px 8px 8px', border: 'none', borderRadius: 18, background: on ? C.selectedRow : 'transparent', textAlign: 'left', cursor: busy ? 'default' : 'pointer', transition: 'background-color .15s', opacity: busy ? .6 : 1 }}>
                  {f.avatarUrl
                    ? <img src={f.avatarUrl} alt="" referrerPolicy="no-referrer" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 999, display: 'block' }} />
                    : <span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 999, background: C.field, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: C.gray400 }}>{(f.displayName || '?').charAt(0).toUpperCase()}</span>}
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#000' }}>{f.displayName}</span>
                    {f.handle && <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.1em', color: C.gray400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'uppercase' }}>@{f.handle}</span>}
                  </span>
                  {busy ? (
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: f.status === 'applied' ? C.greenFg : C.gray400, textTransform: 'uppercase' }}>{f.status}</span>
                  ) : (
                    <span aria-hidden="true" style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 999, boxSizing: 'border-box', border: `2px solid ${on ? C.pink : '#D1D5DB'}`, background: on ? C.pink : '#fff' }}>{on && <Check />}</span>
                  )}
                </button>
              );
            })}
            {!friendsLoading && visible.length === 0 && (
              <span style={{ padding: '16px 8px', fontSize: 12, color: C.gray500 }}>{q.trim() ? `No friends match "${q.trim()}".` : 'Add friends on Portal to send it straight to them.'}</span>
            )}
          </div>
          <span style={{ fontSize: 11, lineHeight: 1.6, color: C.gray500 }}>Lands in their Portal inbox with a link they can open and grab a spot from.</span>
          {sendError && <span role="alert" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em', color: '#B91C1C', textTransform: 'uppercase' }}>{sendError}</span>}
          {(anyPicked || sent > 0) && (
            <Pill variant="primary" on="white" size={48} fontSize={12} tracking=".16em" onClick={send} disabled={sending || (!anyPicked && sent > 0)} className="pk-rise-fast" style={{ width: '100%', background: sent > 0 && !anyPicked ? C.green : undefined, opacity: 1 }}>
              {sending ? 'SENDING…' : sent > 0 && !anyPicked ? `SENT TO ${sent} ✓` : `SEND TO ${n} FRIEND${n === 1 ? '' : 'S'}`}
            </Pill>
          )}
        </section>

        {event?.fundingType === 'party_starter' && (
          <Pill variant="secondary" on="white" size={48} onClick={() => setPsShareOpen(true)} style={{ width: '100%' }}>SHARE EVENT CARD</Pill>
        )}
        <Pill variant="primary" on="white" size={52} onClick={shareTo} style={{ width: '100%', gap: 10 }}><ShareGlyph />SHARE TO...</Pill>
        <button type="button" onClick={() => navigate(`/host/events/${eventId}`)} className="pk pk-btn pk-text-on-white" style={{ alignSelf: 'center', padding: '6px 12px', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase' }}>GO TO EVENT DASHBOARD →</button>
      </main>

      <ShareSheet isOpen={shareOpen} onClose={() => setShareOpen(false)} title={event?.title || ''} venue={event?.venueName} city={event?.locationCity} url={url} inviteCode={code} date={event?.date || event?.dateTime} status={event?.status} mode={event?.mode} eventId={event?.id} eventService={eventService} posterUrl={event?.posterUrl} cardUrl={eventCardUrl(event?.inviteCode)} />
      {psShareOpen && event && <PartyStarterShareModal event={event} onClose={() => setPsShareOpen(false)} />}
    </>,
  );
}
